import { useCallback, useEffect, useState } from "react";
import {
  Users,
  RefreshCw,
  Loader2,
  AlertCircle,
  Sparkles,
  Search,
  CheckCircle2,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { adminApi, formatCOP } from "../../lib/api";
import { norm } from "../../lib/searchUtils";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return iso;
  }
};

const FILTERS = [
  { value: "all", label: "Todos" },
  { value: "activos", label: "Activos" },
  { value: "pending", label: "Pendientes de revisión" },
  { value: "inactivos", label: "Inactivos" },
];

export default function AdminVendedoresCatalogo() {
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [savingCode, setSavingCode] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [nitDraft, setNitDraft] = useState({}); // {code: nit_string}
  const [resolvingCode, setResolvingCode] = useState(null);
  const [supDraft, setSupDraft] = useState({}); // {code: {code, nombre}}
  const [savingSup, setSavingSup] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/vendedores");
      setData(data || { items: [], summary: {} });
    } catch {
      toast.error("No pudimos cargar el catálogo de vendedores");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDiscover = useCallback(async () => {
    setDiscovering(true);
    try {
      const { data } = await adminApi.post("/admin/vendedores/discover");
      if (data.discovered > 0) {
        toast.success(`${data.discovered} vendedor(es) nuevo(s) detectado(s). Revisa y aprueba abajo.`);
      } else {
        toast.info("No se detectaron códigos de vendedor nuevos.");
      }
      await fetchList();
    } catch (e) {
      toast.error("Falló el auto-descubrimiento: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDiscovering(false);
    }
  }, [fetchList]);

  const [rebuilding, setRebuilding] = useState(false);
  const handleRebuildCache = useCallback(async () => {
    setRebuilding(true);
    try {
      const { data } = await adminApi.post("/admin/vendedores/rebuild-cache");
      toast.success(`Cache reconstruido: ${data.total} vendedores · ${data.activos_comerciales} activos comerciales`);
      await fetchList();
    } catch (e) {
      toast.error("Falló el rebuild: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRebuilding(false);
    }
  }, [fetchList]);

  const toggleField = useCallback(async (code, field, value) => {
    setSavingCode(code);
    try {
      await adminApi.patch(`/admin/vendedores/${code}`, { [field]: value });
      toast.success("Cambio guardado");
      await fetchList();
    } catch (e) {
      toast.error("No se pudo guardar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingCode(null);
    }
  }, [fetchList]);

  const handleResolveNit = useCallback(async (code) => {
    const nit = (nitDraft[code] || "").trim();
    if (!nit) {
      toast.error("Ingresa un NIT");
      return;
    }
    setResolvingCode(code);
    try {
      const { data } = await adminApi.post(`/admin/vendedores/${code}/resolve-nit`, { nit });
      toast.success(`Vinculado a ${data.data.nombre}`);
      setNitDraft((prev) => ({ ...prev, [code]: "" }));
      await fetchList();
    } catch (e) {
      toast.error("No se pudo vincular: " + (e?.response?.data?.detail || e.message));
    } finally {
      setResolvingCode(null);
    }
  }, [nitDraft, fetchList]);

  const saveSupervisor = useCallback(async (code, current) => {
    const draft = supDraft[code] || {};
    const supervisor_code = (draft.code ?? current.supervisor_code ?? "").trim();
    const supervisor_nombre = (draft.nombre ?? current.supervisor_nombre ?? "").trim();
    setSavingSup(code);
    try {
      await adminApi.patch(`/admin/vendedores/${code}`, { supervisor_code, supervisor_nombre });
      toast.success("Supervisor guardado");
      setSupDraft((p) => { const n = { ...p }; delete n[code]; return n; });
      await fetchList();
    } catch (e) {
      toast.error("No se pudo guardar el supervisor: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingSup(null);
    }
  }, [supDraft, fetchList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/vendedores");
        if (!cancelled) setData(data || { items: [], summary: {} });
      } catch {
        if (!cancelled) toast.error("No pudimos cargar el catálogo de vendedores");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const s = data.summary || {};

  const filterItems = (allItems) => allItems.filter((x) => {
    if (filter === "activos" && !x.activo) return false;
    if (filter === "inactivos" && x.activo) return false;
    if (filter === "pending" && !x.pending_review) return false;
    const q = norm(search.trim());
    if (q) {
      const matches =
        norm(x.nombre || "").includes(q) ||
        (x.nit || "").includes(q) ||
        (x.code || "").includes(q);
      if (!matches) return false;
    }
    return true;
  });
  const filteredItems = filterItems(data.items || []);

  return (
    <div data-testid="admin-vendedores-catalogo-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">
            Catálogo Arcos
          </p>
          <h1 className="font-serif text-3xl mt-1 tracking-tight flex items-center gap-3">
            <Users className="h-7 w-7 text-cm-yellow" />
            Vendedores de Arcos
          </h1>
          <p className="text-sm text-cm-ink-soft mt-2 max-w-2xl">
            Códigos del módulo <span className="font-semibold">Terceros → Vendedores</span> de Arcos.
            Cuando el sync detecte un código nuevo en los pedidos, aparecerá aquí pendiente de revisión.
            Decide quién cuenta como vendedor activo comercial en el dashboard.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchList}
            className="border-cm-border"
            data-testid="refresh-list-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button
            onClick={handleDiscover}
            disabled={discovering}
            className="bg-cm-ink text-cm-cream hover:bg-cm-ink/90"
            data-testid="discover-btn"
          >
            {discovering ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Auto-descubrir
          </Button>
          <Button
            variant="outline"
            onClick={handleRebuildCache}
            disabled={rebuilding}
            className="border-cm-red text-cm-red hover:bg-cm-red hover:text-white"
            data-testid="rebuild-cache-btn"
            title="Fuerza reconstrucción del cache que usa el dashboard comercial. Úsalo tras cambiar activo/comercial en producción."
          >
            {rebuilding ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Aplicar cambios al dashboard
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="p-4 bg-white border-cm-border shadow-none">
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Total catálogo</p>
          <p className="font-mono text-2xl font-bold mt-1">{s.total ?? 0}</p>
        </Card>
        <Card className="p-4 bg-white border-cm-border shadow-none">
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Activos</p>
          <p className="font-mono text-2xl font-bold mt-1 text-emerald-700">{s.activos ?? 0}</p>
        </Card>
        <Card className="p-4 bg-white border-cm-border shadow-none">
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Activos comerciales</p>
          <p className="font-mono text-2xl font-bold mt-1 text-cm-red">{s.activos_comerciales ?? 0}</p>
        </Card>
        <Card
          className={`p-4 border-cm-border shadow-none ${
            s.pending_review > 0 ? "bg-amber-50 border-amber-300" : "bg-white"
          }`}
        >
          <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Pendientes</p>
          <p
            className={`font-mono text-2xl font-bold mt-1 ${
              s.pending_review > 0 ? "text-amber-700" : ""
            }`}
          >
            {s.pending_review ?? 0}
          </p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              data-testid={`filter-${f.value}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === f.value
                  ? "bg-cm-ink text-cm-cream"
                  : "bg-white border border-cm-border text-cm-ink-soft hover:text-cm-ink"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm ml-auto">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-cm-ink-soft" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, NIT o código…"
            className="pl-9 bg-white border-cm-border"
            data-testid="search-input"
          />
        </div>
      </div>

      {loading ? (
        <Card className="p-12 text-center bg-white border-cm-border shadow-none">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
          <p className="text-sm text-cm-ink-soft mt-3">Cargando catálogo…</p>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card className="p-12 text-center bg-white border-cm-border shadow-none">
          <Users className="h-12 w-12 mx-auto text-cm-ink-soft/40" />
          <h2 className="font-serif text-xl mt-4">No hay vendedores con ese filtro</h2>
          <p className="text-sm text-cm-ink-soft mt-2">
            Cambia el filtro o búsqueda. Si esperas ver alguien nuevo, dale clic a &quot;Auto-descubrir&quot;.
          </p>
        </Card>
      ) : (
        <Card className="bg-white border-cm-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vendedores-table">
              <thead className="bg-cm-cream-alt border-b border-cm-border">
                <tr className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft">
                  <th className="px-4 py-3">Código</th>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">NIT</th>
                  <th className="px-4 py-3">Supervisor (código · nombre)</th>
                  <th className="px-4 py-3 text-right">Pedidos</th>
                  <th className="px-4 py-3 text-right">Facturado</th>
                  <th className="px-4 py-3">Último</th>
                  <th className="px-4 py-3 text-center">Activo</th>
                  <th className="px-4 py-3 text-center">Comercial</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cm-border">
                {filteredItems.map((v) => (
                  <tr
                    key={v.code}
                    className={`${v.pending_review ? "bg-amber-50/50" : ""} hover:bg-cm-cream-alt/30 transition-colors`}
                    data-testid={`vendedor-row-${v.code}`}
                  >
                    <td className="px-4 py-3 font-mono font-bold">{v.code}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-cm-ink">
                        {v.nombre || (v.nombre_pedido_hint
                          ? <span className="italic text-cm-ink-soft">(sin nombre oficial · resolver NIT)</span>
                          : "")}
                      </div>
                      {!v.nombre && v.nombre_pedido_hint && (
                        <div className="text-[10px] text-cm-ink-soft mt-0.5" data-testid={`vendedor-hint-${v.code}`}>
                          pista del pedido (NO oficial): {v.nombre_pedido_hint}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        {v.pending_review && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-semibold uppercase tracking-widest">
                            <AlertCircle className="h-3 w-3" /> Por revisar
                          </span>
                        )}
                        {v.source === "auto" && !v.pending_review && (
                          <span className="text-[10px] text-cm-ink-soft uppercase tracking-widest">
                            Auto
                          </span>
                        )}
                        {v.cargo && (
                          <span className="text-[10px] text-cm-ink-soft uppercase tracking-widest">
                            · {v.cargo}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {v.nit ? (
                        <span className="text-cm-ink-soft">{v.nit}</span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={nitDraft[v.code] || ""}
                            onChange={(e) => setNitDraft((prev) => ({ ...prev, [v.code]: e.target.value }))}
                            placeholder="NIT…"
                            disabled={resolvingCode === v.code}
                            className="w-28 px-2 py-1 border border-amber-300 rounded text-xs font-mono bg-white focus:outline-none focus:border-amber-500"
                            data-testid={`nit-input-${v.code}`}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleResolveNit(v.code);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleResolveNit(v.code)}
                            disabled={resolvingCode === v.code || !(nitDraft[v.code] || "").trim()}
                            className="px-2 py-1 bg-amber-600 text-white text-[10px] uppercase tracking-widest rounded disabled:opacity-40 hover:bg-amber-700 transition-colors"
                            data-testid={`resolve-nit-btn-${v.code}`}
                          >
                            {resolvingCode === v.code ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              "Vincular"
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={supDraft[v.code]?.code ?? v.supervisor_code ?? ""}
                          onChange={(e) => setSupDraft((p) => ({ ...p, [v.code]: { ...(p[v.code] || {}), code: e.target.value } }))}
                          placeholder="Cód."
                          className={`w-16 px-2 py-1 border rounded text-xs font-mono bg-white focus:outline-none ${(v.supervisor_code || "").trim() ? "border-cm-border" : "border-amber-300"}`}
                          data-testid={`sup-code-${v.code}`}
                        />
                        <input
                          type="text"
                          value={supDraft[v.code]?.nombre ?? v.supervisor_nombre ?? ""}
                          onChange={(e) => setSupDraft((p) => ({ ...p, [v.code]: { ...(p[v.code] || {}), nombre: e.target.value } }))}
                          placeholder="Nombre supervisor"
                          className={`w-40 px-2 py-1 border rounded text-xs bg-white focus:outline-none ${(v.supervisor_nombre || "").trim() ? "border-cm-border" : "border-amber-300"}`}
                          data-testid={`sup-name-${v.code}`}
                        />
                        {supDraft[v.code] && (
                          <button
                            type="button"
                            onClick={() => saveSupervisor(v.code, v)}
                            disabled={savingSup === v.code}
                            className="px-2 py-1 bg-cm-ink text-cm-cream text-[10px] uppercase tracking-widest rounded disabled:opacity-40 hover:bg-cm-ink/90"
                            data-testid={`sup-save-${v.code}`}
                          >
                            {savingSup === v.code ? <Loader2 className="h-3 w-3 animate-spin" /> : "OK"}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {v.order_count?.toLocaleString("es-CO") || 0}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">
                      {formatCOP(v.total_amount || 0)}
                    </td>
                    <td className="px-4 py-3 text-xs text-cm-ink-soft">
                      {formatDate(v.last_order_date)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <Switch
                          checked={!!v.activo}
                          disabled={savingCode === v.code}
                          onCheckedChange={(val) => toggleField(v.code, "activo", val)}
                          data-testid={`toggle-activo-${v.code}`}
                          aria-label={`Activar ${v.nombre}`}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <Switch
                          checked={!!v.es_comercial}
                          disabled={savingCode === v.code}
                          onCheckedChange={(val) => toggleField(v.code, "es_comercial", val)}
                          data-testid={`toggle-comercial-${v.code}`}
                          aria-label={`Marcar ${v.nombre} como comercial`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-6 p-4 bg-cm-cream-alt border border-cm-border rounded-md text-xs text-cm-ink-soft leading-relaxed">
        <p className="font-semibold text-cm-ink mb-1 flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" /> ¿Cómo funciona?
        </p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Arcos asigna a cada pedido un <span className="font-mono">PedidoVendedorId</span> que es el código del módulo <em>Terceros → Vendedores</em>.</li>
          <li>Cuando ingresa un vendedor nuevo a la empresa, su nuevo código aparecerá aquí marcado como <span className="text-amber-700 font-semibold">&quot;Por revisar&quot;</span> tras el siguiente sync.</li>
          <li>Las filas pendientes tienen un campo <strong>NIT</strong> editable: escribe el NIT del vendedor y pulsa <strong>Vincular</strong> para que el sistema busque su Tercero en Arcos y complete los datos canónicos.</li>
          <li>Marca <strong>Activo</strong> + <strong>Comercial</strong> para que aparezca en el Dashboard Comercial. Sus pedidos se acumulan retroactivamente.</li>
          <li>Si un vendedor sale de la empresa, desactiva su <strong>Activo</strong> para que deje de aparecer (sus pedidos históricos se conservan).</li>
        </ul>
      </div>
    </div>
  );
}
